/**
 * @file apps/web/src/pages/DocumentsPage.tsx
 * @description Documents (FDD) for a project.
 * Lists documents with a sidebar; clicking one opens it in FDDEditor for per-section editing.
 * Supports AI section regeneration and document generation via the pipeline.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { notifyProjectOverviewChanged } from '../lib/projectOverviewEvents.js';
import { Plus, FileText, AlertCircle, ChevronRight, Sparkles, FileDown, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { documentsApi, approvalsApi } from '../lib/api.js';
import { FDDEditor, type FDDSection } from '../components/FDDEditor.js';
import { Badge } from '../components/Badge.js';
import { SubmitForApprovalPanel } from '../components/SubmitForApprovalPanel.js';
import { ApprovalStepper, type ApprovalStep } from '../components/ApprovalStepper.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Document {
  id:           string;
  title:        string;
  doc_type:     string;
  status:       string;
  created_at:   string;
  updated_at:   string;
}

// ---------------------------------------------------------------------------
// Document sidebar item
// ---------------------------------------------------------------------------

function DocItem({
  doc,
  active,
  onClick,
}: {
  doc:     Document;
  active:  boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left p-3 rounded-lg border transition-colors',
        active
          ? 'border-blue bg-sky'
          : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/30',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={['text-sm font-medium truncate', active ? 'text-blue' : 'text-ink'].join(' ')}>
            {doc.title}
          </p>
          <p className="text-[11px] font-mono text-muted mt-0.5">{doc.doc_type.toUpperCase()}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant={doc.status as never} size="xs" />
          {active && <ChevronRight size={12} className="text-blue" />}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// DocumentsTabContent
// ---------------------------------------------------------------------------

export function DocumentsTabContent() {
  const { projectId } = useParams<{ projectId: string }>();
  const { devHeaders } = useAuth();
  // ?doc=<id> deep-links a specific document (DiscoveryPanel.tsx's "view
  // document" link, docs/NEXT-PHASE-SCOPING.md §2) — read once on load
  // below; falls back to the first document exactly as before when absent
  // or when the id it names isn't in this project's list.
  const [searchParams] = useSearchParams();
  const deepLinkedDocId = searchParams.get('doc');

  const [docs, setDocs]           = useState<Document[]>([]);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [sections, setSections]   = useState<FDDSection[]>([]);
  const [loading, setLoading]     = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fddApproval, setFddApproval] = useState<{ id: string; status: string; workflow_type: string; steps: ApprovalStep[] } | null>(null);
  const [showSubmitPanel, setShowSubmitPanel] = useState(false);

  // Load document list
  const loadDocs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await documentsApi.list(projectId, devHeaders) as Document[];
      setDocs(data);
      if (data.length > 0 && !activeDocId) {
        const deepLinked = deepLinkedDocId && data.find((d) => d.id === deepLinkedDocId);
        setActiveDocId(deepLinked ? deepLinked.id : data[0]!.id);
      }
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Load sections when active doc changes
  useEffect(() => {
    if (!activeDocId) return;
    let cancelled = false;
    setSectionsLoading(true);
    documentsApi.getSections(activeDocId, devHeaders)
      .then((data) => {
        if (!cancelled) setSections(data as FDDSection[]);
      })
      .catch(() => {
        if (!cancelled) setSections([]);
      })
      .finally(() => {
        if (!cancelled) setSectionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the FDD approval (if any) for the active document, to derive lock state
  useEffect(() => {
    setShowSubmitPanel(false);
    if (!activeDocId) { setFddApproval(null); return; }
    let cancelled = false;
    approvalsApi.forEntity('fdd_approval', activeDocId, devHeaders)
      .then((data) => { if (!cancelled) setFddApproval(data as typeof fddApproval); })
      .catch(() => { if (!cancelled) setFddApproval(null); });
    return () => { cancelled = true; };
  }, [activeDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save section
  const handleSave = async (key: string, contentMd: string) => {
    if (!activeDocId) return;
    await documentsApi.updateSection(activeDocId, key, { content_md: contentMd }, devHeaders);
    setSections((prev) =>
      prev.map((s) => s.section_key === key ? { ...s, content_md: contentMd } : s)
    );
  };

  // Regenerate section via AI (stub)
  const handleRegenerate = async (key: string) => {
    if (!activeDocId) return;
    const updated = await documentsApi.regenSection(activeDocId, key, {}, devHeaders) as FDDSection;
    setSections((prev) =>
      prev.map((s) => s.section_key === key ? { ...s, ...updated } : s)
    );
  };

  // Generate new document
  const handleGenerate = async () => {
    if (!projectId) return;
    setGenerating(true);
    try {
      const created = await documentsApi.generate({
        projectId,
        docType: 'fdd',
        title:   'Functional Design Document',
      }, devHeaders) as Document;
      setDocs((prev) => [created, ...prev]);
      setActiveDocId(created.id);
      notifyProjectOverviewChanged(projectId); // the project-wide FDD feeds the Design/UAT gates
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  // Export the active document as a Word (.docx) file
  const handleExportDocx = async () => {
    if (!activeDocId || !activeDoc) return;
    setExporting(true);
    try {
      const blob = await documentsApi.exportDocx(activeDocId, devHeaders);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeDoc.title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'document'}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export document');
    } finally {
      setExporting(false);
    }
  };

  const activeDoc = docs.find((d) => d.id === activeDocId);

  const isLocked = !!fddApproval && ['pending', 'in_progress', 'approved'].includes(fddApproval.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Documents</h2>
          {!loading && (
            <p className="text-xs font-mono text-muted mt-0.5">
              {docs.length} document{docs.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary text-sm"
        >
          {generating
            ? <><span className="animate-spin inline-block mr-1">⟳</span> Generating…</>
            : <><Sparkles size={14} /> Generate FDD</>
          }
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="card h-64 animate-pulse bg-gray/60" />
      ) : docs.length === 0 ? (
        <div className="empty-state py-16">
          <FileText size={32} className="text-muted/30 mb-3 mx-auto" />
          <p className="text-sm text-muted">No documents yet.</p>
          <p className="text-xs text-muted/70 mt-1">
            Upload a meeting recording or click "Generate FDD" to create the first document.
          </p>
          <button onClick={handleGenerate} disabled={generating} className="btn-secondary mt-4">
            <Sparkles size={14} /> Generate FDD
          </button>
        </div>
      ) : (
        <div className="flex gap-4 items-start">
          {/* Document list sidebar */}
          <aside className="w-56 shrink-0 space-y-2">
            <p className="section-label px-1">Documents</p>
            {docs.map((doc) => (
              <DocItem
                key={doc.id}
                doc={doc}
                active={doc.id === activeDocId}
                onClick={() => setActiveDocId(doc.id)}
              />
            ))}
            <button
              onClick={() => {
                const title = prompt('Document title:');
                if (!title?.trim()) return;
                documentsApi.create({
                  projectId,
                  title:   title.trim(),
                  docType: 'fdd',
                }, devHeaders)
                  .then((d) => {
                    setDocs((prev) => [...prev, d as Document]);
                    setActiveDocId((d as Document).id);
                    if (projectId) notifyProjectOverviewChanged(projectId);
                  })
                  .catch(() => undefined);
              }}
              className="w-full flex items-center gap-1.5 text-xs text-muted hover:text-blue transition-colors px-3 py-2 rounded border border-dashed border-[rgba(8,17,31,0.1)] hover:border-blue/30"
            >
              <Plus size={12} /> Add Document
            </button>
          </aside>

          {/* Editor pane */}
          <div className="flex-1 min-w-0">
            {activeDoc && (
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-display text-base font-semibold text-ink truncate">{activeDoc.title}</h3>
                  <Badge variant={activeDoc.status as never} size="xs" />
                  <span className="text-[11px] font-mono text-muted">{activeDoc.doc_type.toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isLocked && !showSubmitPanel && (
                    <button
                      onClick={() => setShowSubmitPanel(true)}
                      className="btn-secondary text-xs py-1 px-2"
                    >
                      <ShieldCheck size={13} /> Submit for Approval
                    </button>
                  )}
                  <button
                    onClick={() => void handleExportDocx()}
                    disabled={exporting || sections.length === 0}
                    className="btn-secondary text-xs py-1 px-2"
                    title={sections.length === 0 ? 'No sections to export yet' : 'Export as Word document'}
                  >
                    {exporting
                      ? <Loader2 size={13} className="animate-spin" />
                      : <FileDown size={13} />
                    }
                    {exporting ? 'Exporting…' : 'Export Word'}
                  </button>
                </div>
              </div>
            )}

            {showSubmitPanel && activeDocId && projectId && (
              <div className="mb-3">
                <SubmitForApprovalPanel
                  projectId={projectId}
                  entityType="fdd_approval"
                  entityId={activeDocId}
                  title={`FDD Approval — ${activeDoc?.title ?? ''}`}
                  onCreated={(created) => {
                    setFddApproval(created as typeof fddApproval);
                    setShowSubmitPanel(false);
                    // Submitting puts the FDD's design item in review.
                    notifyProjectOverviewChanged(projectId);
                  }}
                  onCancel={() => setShowSubmitPanel(false)}
                />
              </div>
            )}

            {isLocked && fddApproval && (
              <div className="mb-3 card p-4 space-y-3 border-warning/20 bg-warning/6">
                <p className="text-sm font-semibold text-ink">
                  Locked — FDD approval is {fddApproval.status}
                </p>
                <ApprovalStepper
                  steps={fddApproval.steps}
                  workflowType={fddApproval.workflow_type as 'sequential' | 'parallel' | 'conditional'}
                />
              </div>
            )}

            <FDDEditor
              docId={activeDocId ?? ''}
              sections={sections}
              loading={sectionsLoading}
              readOnly={isLocked}
              onSave={handleSave}
              onRegenerate={isLocked ? undefined : handleRegenerate}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocumentsPage — standalone /documents route wrapper. Design page's
// "Documents (FDD)" tab renders DocumentsTabContent directly instead.
// ---------------------------------------------------------------------------

export function DocumentsPage() {
  return <DocumentsTabContent />;
}
